import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, ClipboardCopy, Link2 } from "lucide-react";

const API = `${window.location.origin}/api`;

const GOLD = "#C4A671";
const AXES = ["estetica", "armonia", "affinita", "equilibrio"];

function PendingCard({ d, authHeader, onChanged }) {
  const [s, setS] = useState(
    d.sensory || { estetica: 85, armonia: 85, affinita: 85, equilibrio: 85, voto: 8.5 }
  );
  const [busy, setBusy] = useState(false);
  const dirty = useRef(false);

  useEffect(() => {
    if (d.sensory && !dirty.current) setS(d.sensory);
  }, [d.sensory]);

  const setAxis = (k) => (e) => {
    dirty.current = true;
    setS((p) => ({ ...p, [k]: Number(e.target.value) }));
  };

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/registry/dishes/${d.id}`, body, authHeader);
      toast.success(msg);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento.");
    } finally {
      setBusy(false);
    }
  };

  const revaluate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/registry/dishes/${d.id}/evaluate`, {}, authHeader);
      toast.success("Perizia AI avviata — ricarica tra qualche secondo.");
      setTimeout(onChanged, 12000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio perizia.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-[#C4A671]/30 bg-[#0F0E0B] p-4 flex flex-col md:flex-row gap-5"
      data-testid={`pending-card-${d.id}`}
    >
      <img src={d.photo_url} alt={d.title} className="w-full md:w-52 aspect-[4/3] object-cover border border-[#222]" />
      <div className="flex-1 min-w-0">
        <div className="font-serif-display text-[18px] text-[#F2EFE8]">{d.title}</div>
        <div className="text-[11.5px] text-[#8B7A55] mt-1">
          {d.business_name || "—"} · {d.city}, {d.country} · {d.pairing?.wine} · Codice{" "}
          {new Date(d.created_at).toLocaleString("it-IT")}
        </div>
        <div className="text-[12px] text-[#C9C3B5] mt-2">
          {(d.ingredients || []).join(" · ")}
        </div>

        {d.ai_evaluation?.perizia ? (
          <p className="mt-3 text-[12px] italic leading-[1.7] text-[#B0A88F] border-l-2 pl-3" style={{ borderColor: GOLD }}>
            «{d.ai_evaluation.perizia}»
          </p>
        ) : (
          <p className="mt-3 text-[11.5px] italic text-[#7A7568]">
            ⏳ Perizia AI in corso… (ricarica o rilancia se non appare)
          </p>
        )}

        <div className="flex flex-wrap items-end gap-3 mt-4">
          {AXES.map((k) => (
            <label key={k} className="w-[70px]">
              <span className="overline text-[8px] text-[#7A7568] block mb-1 capitalize">{k}</span>
              <input
                type="number" min={0} max={100}
                data-testid={`pending-${k}-${d.id}`}
                value={s[k] ?? ""}
                onChange={setAxis(k)}
                className="w-full bg-[#111] border border-[#2a2a2a] px-2 py-1.5 text-[13px] text-[#F2EFE8] outline-none focus:border-[#C4A671]"
              />
            </label>
          ))}
          <label className="w-[70px]">
            <span className="overline text-[8px] block mb-1" style={{ color: GOLD }}>Voto</span>
            <input
              type="number" min={0} max={10} step={0.1}
              data-testid={`pending-voto-${d.id}`}
              value={s.voto ?? ""}
              onChange={(e) => { dirty.current = true; setS((p) => ({ ...p, voto: Number(e.target.value) })); }}
              className="w-full bg-[#111] border border-[#C4A671]/40 px-2 py-1.5 text-[13px] outline-none focus:border-[#C4A671]"
              style={{ color: GOLD }}
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <button
            data-testid={`pending-approve-${d.id}`}
            disabled={busy}
            onClick={() => patch({ status: "published", sensory: s }, `"${d.title}" pubblicato nel Registro.`)}
            className="px-5 py-2 text-[9.5px] tracking-[0.2em] uppercase disabled:opacity-40"
            style={{ background: GOLD, color: "#0A0A0A" }}
          >
            ✓ Approva e Pubblica
          </button>
          <button
            data-testid={`pending-revaluate-${d.id}`}
            disabled={busy}
            onClick={revaluate}
            className="px-4 py-2 text-[9.5px] tracking-[0.2em] uppercase border border-[#C4A671]/50 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-40"
          >
            ↻ Rilancia Perizia AI
          </button>
          <button
            data-testid={`pending-archive-${d.id}`}
            disabled={busy}
            onClick={() => patch({ status: "archived" }, `"${d.title}" archiviato.`)}
            className="px-4 py-2 text-[9.5px] tracking-[0.2em] uppercase border border-[#333] text-[#7A7568] hover:text-[#C9C3B5] transition disabled:opacity-40"
          >
            Archivia
          </button>
        </div>
      </div>
    </div>
  );
}

function CodesSection({ authHeader }) {
  const [codes, setCodes] = useState([]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/registry/codes`, authHeader);
      setCodes(data?.codes || []);
    } catch {
      toast.error("Errore caricamento codici.");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = async (c) => {
    try {
      await axios.patch(`${API}/admin/registry/codes/${c.id}`, { active: !c.active }, authHeader);
      setCodes((p) => p.map((x) => (x.id === c.id ? { ...x, active: !c.active } : x)));
      toast.success(!c.active ? `Codice ${c.code} riattivato.` : `Codice ${c.code} revocato.`);
    } catch {
      toast.error("Errore aggiornamento codice.");
    }
  };

  if (codes.length === 0) {
    return (
      <p className="text-[12.5px] text-[#7A7568] border border-[#1f1f1f] px-4 py-5">
        Nessun codice generato. Genera il primo dalla scheda di un cliente in Archivio
        con il pulsante «✦ Genera &amp; Invia Codice».
      </p>
    );
  }
  return (
    <div className="border border-[#1f1f1f] divide-y divide-[#1f1f1f]" data-testid="codes-list">
      {codes.map((c) => (
        <div key={c.id} className={`flex items-center gap-4 px-4 py-3 ${!c.active ? "opacity-50" : ""}`}
             data-testid={`code-row-${c.code}`}>
          <span className="font-mono text-[13px] tracking-[0.15em]" style={{ color: GOLD }}>{c.code}</span>
          <div className="flex-1 min-w-0">
            <span className="text-[13px] text-[#F2EFE8]">{c.business_name}</span>
            <span className="text-[11px] text-[#7A7568] ml-2">
              {c.city} · {c.email} · {new Date(c.created_at).toLocaleDateString("it-IT")}
            </span>
          </div>
          <span className="overline text-[9px]" style={{ color: c.uploads_used >= c.max_uploads ? "#B08D57" : "#7A7568" }}>
            {c.uploads_used}/{c.max_uploads} usi
          </span>
          <button
            data-testid={`code-toggle-${c.code}`}
            onClick={() => toggle(c)}
            className="overline text-[8.5px] px-3 py-1.5 border transition"
            style={c.active
              ? { borderColor: "rgba(196,166,113,0.5)", color: GOLD }
              : { borderColor: "#333", color: "#7A7568" }}
          >
            {c.active ? "Attivo" : "Revocato"}
          </button>
        </div>
      ))}
    </div>
  );
}

export default function RegistryPanel({ authHeader }) {
  const [dishes, setDishes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const dragId = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/registry/dishes`, authHeader);
      setDishes(data?.dishes || []);
    } catch {
      toast.error("Errore caricamento registro.");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onDragStart = (id) => () => { dragId.current = id; };
  const onDragEnter = (id) => (e) => {
    e.preventDefault();
    if (!dragId.current || dragId.current === id) return;
    setDishes((prev) => {
      const arr = [...prev];
      const from = arr.findIndex((x) => x.id === dragId.current);
      const to = arr.findIndex((x) => x.id === id);
      if (from < 0 || to < 0) return prev;
      arr.splice(to, 0, arr.splice(from, 1)[0]);
      return arr;
    });
  };
  const onDragEnd = () => {
    if (!dragId.current) return;
    dragId.current = null;
    setDishes((prev) => {
      axios
        .put(`${API}/admin/registry/dishes/reorder`, { ids: prev.map((x) => x.id) }, authHeader)
        .then(() => toast.success("Ordine del Registro salvato."))
        .catch(() => { toast.error("Errore salvataggio ordine."); load(); });
      return prev;
    });
  };

  const toggleStatus = async (d) => {
    const status = d.status === "published" ? "archived" : "published";
    try {
      await axios.patch(`${API}/admin/registry/dishes/${d.id}`, { status }, authHeader);
      setDishes((p) => p.map((x) => (x.id === d.id ? { ...x, status } : x)));
      toast.success(status === "published" ? `"${d.title}" pubblicato.` : `"${d.title}" archiviato.`);
    } catch {
      toast.error("Errore aggiornamento stato.");
    }
  };

  const saveTitle = async (d) => {
    const title = editTitle.trim();
    setEditId(null);
    if (!title || title === d.title) return;
    try {
      await axios.patch(`${API}/admin/registry/dishes/${d.id}`, { title }, authHeader);
      setDishes((p) => p.map((x) => (x.id === d.id ? { ...x, title } : x)));
      toast.success("Titolo aggiornato.");
    } catch {
      toast.error("Errore salvataggio titolo.");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare definitivamente "${d.title}" dal Registro?`)) return;
    try {
      await axios.delete(`${API}/admin/registry/dishes/${d.id}`, authHeader);
      setDishes((p) => p.filter((x) => x.id !== d.id));
      toast.success(`"${d.title}" eliminato.`);
    } catch {
      toast.error("Errore eliminazione.");
    }
  };

  const downloadSocial = async (d) => {
    toast.info("Composizione della cornice Maison…");
    try {
      const res = await axios.get(`${API}/admin/registry/dishes/${d.id}/social-photo`, {
        ...authHeader, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${d.title.toLowerCase().replace(/[^a-z0-9àèéìòù]+/gi, "-").replace(/^-+|-+$/g, "")}-iblei.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Foto con cornice scaricata — pronta per Instagram.");
    } catch {
      toast.error("Errore generazione foto.");
    }
  };

  const copyCaption = async (d) => {
    const perizia =
      d.ai_evaluation?.perizia ||
      "Una creazione iscritta al Registro Globale delle Allocazioni, valutata attraverso la Matrice delle Affinità Sensoriali della Maison.";
    const text = `«${d.title}»\n${d.restaurant_display || "Maison Riservata"} · ${d.city}\n\n${perizia}\n\nIn abbinamento: ${d.pairing?.wine} — La Valle degli Iblei\nMatrice delle Affinità Sensoriali · Voto ${d.sensory?.voto ?? "—"}/10\n\n#LaValleDegliIblei #AllocazioniDautore #${(d.pairing?.wine || "").replace(/\s+/g, "")} #FineDining`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Testo del post copiato negli appunti.");
    } catch {
      toast.error("Impossibile copiare negli appunti.");
    }
  };

  const copyLink = async (d) => {
    const url = d
      ? `${window.location.origin}/api/r/${d.id}`
      : `${window.location.origin}/api/r`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link con anteprima social copiato.");
    } catch {
      toast.error("Impossibile copiare il link.");
    }
  };

  const pending = dishes.filter((d) => d.status === "pending");
  const rest = dishes.filter((d) => d.status !== "pending");
  const published = rest.filter((d) => d.status === "published").length;

  return (
    <div className="max-w-4xl" data-testid="registry-panel">
      <h2 className="font-serif-display text-[22px] text-[#F2EFE8] mb-1">
        Registro Allocazioni d&apos;Autore
      </h2>
      <p className="text-[13px] text-[#7A7568] mb-6">
        {published} creazioni pubblicate su /allocazioni
        {pending.length > 0 && (
          <span style={{ color: GOLD }}> · {pending.length} in attesa di perizia</span>
        )}
      </p>

      {/* Pipeline in attesa */}
      <div className="mb-10" data-testid="pending-section">
        <div className="overline text-[9.5px] mb-3" style={{ color: GOLD }}>
          ⏳ In attesa di approvazione ({pending.length})
        </div>
        {pending.length === 0 ? (
          <p className="text-[12.5px] text-[#7A7568] border border-[#1f1f1f] px-4 py-5">
            Nessun piatto in attesa. Quando uno chef carica una creazione col suo
            Codice di Allocazione, apparirà qui con la perizia AI già pronta.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {pending.map((d) => (
              <PendingCard key={d.id} d={d} authHeader={authHeader} onChanged={load} />
            ))}
          </div>
        )}
      </div>

      {/* Codici allocazione */}
      <div className="mb-10">
        <div className="overline text-[9.5px] mb-3 text-[#8B7A55]">✦ Codici Allocazione emessi</div>
        <CodesSection authHeader={authHeader} />
      </div>

      {/* Collezione */}
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-[9.5px] text-[#8B7A55]">
          Collezione del Registro · trascina ⠿ per riordinare il feed
        </div>
        <button
          data-testid="copy-registry-link"
          onClick={() => copyLink(null)}
          className="flex items-center gap-1.5 overline text-[8.5px] text-[#7A7568] hover:text-[#C4A671] transition"
          title="Copia il link del Registro con anteprima nero/oro per social e WhatsApp"
        >
          <Link2 size={13} /> Link social del Registro
        </button>
      </div>
      <div className="border border-[#1f1f1f] divide-y divide-[#1f1f1f]" data-testid="registry-dishes-list">
        {rest.map((d) => (
          <div
            key={d.id}
            draggable
            onDragStart={onDragStart(d.id)}
            onDragEnter={onDragEnter(d.id)}
            onDragOver={(e) => e.preventDefault()}
            onDragEnd={onDragEnd}
            className={`flex items-center gap-4 px-4 py-3 hover:bg-[#111] transition cursor-grab active:cursor-grabbing ${
              d.status !== "published" ? "opacity-50" : ""
            }`}
            data-testid={`registry-row-${d.city.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="text-[#4a4a4a] select-none text-[14px] leading-none" title="Trascina per riordinare">⠿</span>
            <img src={d.photo_url} alt="" className="w-14 h-11 object-cover border border-[#222] shrink-0" loading="lazy" />
            <div className="flex-1 min-w-0">
              {editId === d.id ? (
                <input
                  autoFocus
                  data-testid={`registry-title-input-${d.id}`}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={() => saveTitle(d)}
                  onKeyDown={(e) => e.key === "Enter" && saveTitle(d)}
                  className="w-full bg-[#111] border border-[#C4A671] px-2 py-1 text-[13.5px] text-[#F2EFE8] outline-none"
                />
              ) : (
                <button
                  data-testid={`registry-title-${d.id}`}
                  onClick={() => { setEditId(d.id); setEditTitle(d.title); }}
                  title="Clicca per modificare il titolo"
                  className="text-left text-[14px] text-[#F2EFE8] font-serif-display hover:text-[#C4A671] transition truncate block max-w-full"
                >
                  {d.title}
                </button>
              )}
              <span className="text-[11px] text-[#7A7568]">
                {d.city}, {d.country} · {d.pairing?.wine} {d.pairing?.lot} · voto {d.sensory?.voto ?? "—"}
                {d.source === "seed" ? " · seed" : " · chef"}
              </span>
            </div>
            <button
              data-testid={`registry-status-${d.id}`}
              onClick={() => toggleStatus(d)}
              className="overline text-[8.5px] px-3 py-1.5 border transition shrink-0"
              style={
                d.status === "published"
                  ? { borderColor: "rgba(196,166,113,0.5)", color: GOLD }
                  : { borderColor: "#333", color: "#7A7568" }
              }
              title="Clicca per pubblicare/archiviare"
            >
              {d.status === "published" ? "Pubblicato" : "Archiviato"}
            </button>
            {d.status === "published" && (
              <span className="flex items-center gap-0.5 shrink-0">
                <button
                  data-testid={`social-photo-${d.id}`}
                  onClick={() => downloadSocial(d)}
                  className="p-1.5 text-[#7A7568] hover:text-[#C4A671] transition"
                  title="Scarica Foto con cornice Maison (Instagram)"
                >
                  <Download size={15} />
                </button>
                <button
                  data-testid={`social-copy-${d.id}`}
                  onClick={() => copyCaption(d)}
                  className="p-1.5 text-[#7A7568] hover:text-[#C4A671] transition"
                  title="Copia Testo del post (descrizione + perizia)"
                >
                  <ClipboardCopy size={15} />
                </button>
                <button
                  data-testid={`social-link-${d.id}`}
                  onClick={() => copyLink(d)}
                  className="p-1.5 text-[#7A7568] hover:text-[#C4A671] transition"
                  title="Copia link del piatto con anteprima social"
                >
                  <Link2 size={15} />
                </button>
              </span>
            )}
            <button
              data-testid={`registry-delete-${d.id}`}
              onClick={() => remove(d)}
              className="text-[#5a5a5a] hover:text-[#c0392b] transition text-[15px] px-1 shrink-0"
              title="Elimina"
            >
              ✕
            </button>
          </div>
        ))}
        {rest.length === 0 && (
          <p className="px-4 py-8 text-center text-[13px] text-[#7A7568]">Registro vuoto.</p>
        )}
      </div>
    </div>
  );
}
